import { cn } from "@/lib/utils";
import { centsToUsd } from "@/lib/money";
import { Badge } from "@/components/ui";
import { DEFAULT_PACKAGE_CODE, type Package } from "@/api/types/package";

interface PackagePickerCardsProps {
  packages: Package[];
  selectedId: string | null;
  onSelect: (pkg: Package) => void;
  disabled?: boolean;
}

/**
 * Package picker — Signature is the playbook's default recommendation, so
 * it carries a ★ Recommended badge and is pre-selected when the builder
 * first loads packages (see QuoteBuilderDialog).
 */
export function PackagePickerCards({
  packages,
  selectedId,
  onSelect,
  disabled,
}: PackagePickerCardsProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {packages.map((pkg) => {
        const selected = pkg.id === selectedId;
        const isDefault = pkg.code === DEFAULT_PACKAGE_CODE;
        return (
          <button
            key={pkg.id}
            type="button"
            data-testid={`package-card-${pkg.code}`}
            data-selected={selected}
            disabled={disabled}
            onClick={() => onSelect(pkg)}
            className={cn(
              "flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              selected
                ? "border-primary bg-primary/5 ring-1 ring-primary"
                : "border-border bg-bg-card hover:border-copper/50",
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-text-primary">{pkg.name}</span>
              {isDefault && (
                <Badge variant="primary" size="sm">
                  ★ Recommended
                </Badge>
              )}
            </div>
            <span className="text-lg font-bold text-text-primary">
              {centsToUsd(pkg.base_price_cents)}
            </span>
            {pkg.description && (
              <p className="text-sm text-text-secondary">{pkg.description}</p>
            )}
            {pkg.includes.length > 0 && (
              <ul className="mt-1 space-y-1 text-xs text-text-muted">
                {pkg.includes.map((line, i) => (
                  <li key={i}>• {line}</li>
                ))}
              </ul>
            )}
          </button>
        );
      })}
    </div>
  );
}
